// The persistent lost-access banner (slice-2-onboarding.md §11.4; contracts.md:53
// LostAccessBanner state; D49). Cross-surface: rendered once by AppShell so every authed
// page carries it. Uses brand.css's own .banner/.banner.risk (brand.css:209-216) - no
// new color. Never color-only (WCAG): the status word is always in the text.
import { useEffect, useState } from "react";
import { isErr } from "@nightcrew/shared";
import type { BannerState, LostAccessBanner as LostAccessBannerModel } from "@nightcrew/shared";
import { getConnectionBanner } from "../api/connections.js";
import { isOwnerOrAdmin, useSession } from "../state/session.js";

/** One banner row. `canReconnect` gates the action per §11.4 - viewers see the same
 * message with no Reconnect button, never a hidden banner. */
const BannerRow = ({
  banner,
  canReconnect,
}: {
  readonly banner: LostAccessBannerModel;
  readonly canReconnect: boolean;
}): React.JSX.Element => (
  <div className="banner top risk" role="alert">
    <span className="dot warn" aria-hidden="true" />
    <div className="grow">
      <b>{banner.platform} access expired.</b>{" "}
      {banner.pausedWork ? "The crew paused work until it is restored. " : ""}
      {canReconnect
        ? "Only an owner or admin can reconnect."
        : "Ask an owner or admin to reconnect."}
    </div>
    {canReconnect ? (
      <a className="btn btn-dark btn-sm" href="/connections">
        Reconnect
      </a>
    ) : null}
  </div>
);

/** Pure, presentational: renders nothing when there are no banners (never a stray empty
 * banner shell), otherwise one row per banner. Exported for direct fixture-driven testing. */
export const BannerList = ({
  banners,
  canReconnect,
}: {
  readonly banners: readonly LostAccessBannerModel[];
  readonly canReconnect: boolean;
}): React.JSX.Element | null => {
  if (banners.length === 0) return null;
  return (
    <div className="stack gap8 mb16" data-testid="lost-access-banner-list">
      {banners.map((banner) => (
        <BannerRow key={banner.connectionId} banner={banner} canReconnect={canReconnect} />
      ))}
    </div>
  );
};

/** The connected banner: fetches BannerState and wires the current session's role. */
export const LostAccessBanner = (): React.JSX.Element | null => {
  const { state } = useSession();
  const [bannerState, setBannerState] = useState<BannerState | null>(null);

  useEffect(() => {
    const cancelled = { current: false };
    void (async () => {
      const result = await getConnectionBanner();
      if (!cancelled.current && !isErr(result)) setBannerState(result.value);
    })();
    return () => {
      cancelled.current = true;
    };
  }, []);

  if (bannerState === null) return null;
  return <BannerList banners={bannerState.banners} canReconnect={isOwnerOrAdmin(state)} />;
};
